import React, {useEffect, useState} from 'react';

import {useCurrentUserState} from 'app/current-user/api';
import {InviteNewMemberButton} from 'app/general/structures/InviteNewMemberButton';
import {RolesSelectMulti} from 'app/roles/components/RoleSelect';
import {useAccountSettingsState} from 'app/settings/api';
import {TeamsSelectMulti} from 'app/teams/components/TeamsSelect';
import {PerUserRequired} from 'app/toasts/components/PerUserRequired';
import {UPGRADE_MODALS} from 'app/toasts/slice';
import {UPGRADE as UPGRADE_URL} from 'app/urls';
import {CreateUserModal} from 'app/user/components/CreateUserForm/CreateUserModal';
import {SelectUsersMulti} from 'app/user/components/select';
import {gt} from 'app/utils/i18n';

import {
  ShowAssignRolesButton,
  ShowAssignTeamsButton,
} from './show-assign-buttons';

import {UserSerializedType} from 'sweet-types';

import type {AssigneesByType, AssigneeType} from './types';
import type {ActionMeta} from 'app/components/general/SweetReactSelect';

export interface BaseProps {
  locked?: boolean;
  noMargin?: boolean;
  field?: string | string[];
}

interface Props extends BaseProps {
  assignments?: AssigneesByType;
  onChange: (
    meta: ActionMeta<AssigneeType>,
    ctname: 'user' | 'team' | 'role'
  ) => unknown;
}

const NOT_PERUSER_ACCOUNT_ASSIGN_LIMIT = 2;

export function AssignUsersField({
  assignments = {
    users: [],
    roles: [],
    teams: [],
  },
  onChange,
  locked,
}: Props) {
  const [showAssignedTeams, setShowAssignedTeams] = useState(
    assignments.teams.length > 0
  );
  const [showAssignedRoles, setShowAssignedRoles] = useState(
    assignments.roles.length > 0
  );

  const {is_peruser, owner_email} = useAccountSettingsState().data!;
  const {is_super_manager} = useCurrentUserState().data!;

  const [show, setShow] = useState<boolean>(false);

  useEffect(() => {
    // if a new team is assigned (probably via steps), show the teams selector

    if (assignments.teams.length > 0) {
      setShowAssignedTeams(true);
    }
  }, [assignments.teams.length]);

  useEffect(() => {
    // if a new role is assigned (probably via steps), show the roles selector
    if (assignments.roles.length > 0) {
      setShowAssignedRoles(true);
    }
  }, [assignments.roles.length]);

  function handleCreateUserModal(user: UserSerializedType) {
    const meta: ActionMeta<AssigneeType> = {
      action: 'select-option',
      option: user,
    };
    onChange(meta, 'user');
    setShow(false);
    return Promise.resolve(true);
  }

  // limit the number of assignees for non-peruser account members
  const selectLimit = is_peruser ? undefined : NOT_PERUSER_ACCOUNT_ASSIGN_LIMIT;

  return (
    <>
      <SelectUsersMulti
        disabled={locked}
        refetchDataOnOpen={true}
        selectLimit={selectLimit}
        selectLimitText={
          is_peruser ? null : (
            <UpgradeRequiredMessage owner_email={owner_email} />
          )
        }
        selected={assignments.users}
        onChange={(users, meta) => {
          return Promise.resolve(onChange(meta, 'user'));
        }}
      />
      {locked ? null : (
        <>
          {showAssignedTeams ? (
            <div className="mt-2">
              <TeamsSelectMulti
                canCreate={is_super_manager}
                disabled={false}
                selected={assignments.teams}
                refetchDataOnOpen={true}
                onChange={(teams, meta) =>
                  Promise.resolve(onChange(meta, 'team'))
                }
                placeholder={gt("Type each Team's name")}
              />
            </div>
          ) : (
            <ShowAssignTeamsButton onClick={() => setShowAssignedTeams(true)} />
          )}
          {showAssignedRoles ? (
            <div className="mt-2">
              <RolesSelectMulti
                disabled={false}
                refetchDataOnOpen={true}
                selected={assignments.roles}
                onChange={(roles, meta) =>
                  Promise.resolve(onChange(meta, 'role'))
                }
                placeholder={gt("Type each Role's name")}
              />
            </div>
          ) : (
            <PerUserRequired
              actionToProtect={() => setShowAssignedRoles(true)}
              modalType={UPGRADE_MODALS.ASSIGN_TO_ROLE}
            >
              {(protectedAction) => (
                <ShowAssignRolesButton onClick={protectedAction} />
              )}
            </PerUserRequired>
          )}
        </>
      )}
      <InviteNewMemberButton
        className="btn-sm"
        onClick={(e) => {
          e.preventDefault();
          setShow(true);
        }}
      />

      <CreateUserModal
        show={show}
        setShow={setShow}
        onCreateUser={handleCreateUserModal}
      />
    </>
  );
}

function UpgradeRequiredMessage({owner_email}: {owner_email: string}) {
  const {is_owner} = useCurrentUserState().data!;

  return (
    <span>
      {gt(
        'To assign a task to more than %s users, request a change in your subscription to per user pricing',
        NOT_PERUSER_ACCOUNT_ASSIGN_LIMIT
      )}
      .{' '}
      {is_owner ? (
        <a href={UPGRADE_URL}>{gt('Upgrade now')}</a>
      ) : (
        <a href={`mailto:${owner_email}`}>
          {gt('Contact your account owner to upgrade')}
        </a>
      )}
    </span>
  );
}
